'use client'
import { cn } from '@/lib/utils'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDebounceCallback, useEventListener } from 'usehooks-ts'

interface ReadMoreTextProps {
  content: string
  lines?: number
  defaultLineHeight?: number
  readMoreLabel?: string
  readMoreButtonClassName?: string
  className?: string
}

export default function ReadMoreText({
  content,
  lines = 3,
  defaultLineHeight = 24,
  readMoreLabel = 'Read more',
  readMoreButtonClassName = '',
  className = '',
}: ReadMoreTextProps) {
  const [expanded, setExpanded] = useState(false)
  const [isOverflow, setIsOverflow] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const visibleRef = useRef<HTMLParagraphElement>(null)
  const measureRef = useRef<HTMLParagraphElement>(null)

  const checkOverflow = useCallback(() => {
    const el = measureRef.current
    const container = containerRef.current
    if (!el || !container) return

    const computed = getComputedStyle(el)
    const lineHeight = parseFloat(computed.lineHeight)
    const safeLineHeight = lineHeight > 0 ? lineHeight : defaultLineHeight
    const containerWidth = container.getBoundingClientRect().width

    el.style.width = `${containerWidth}px`

    const fullHeight = el.getBoundingClientRect().height
    const nowHeight = safeLineHeight * lines

    setIsOverflow(fullHeight > nowHeight + 0.5)
  }, [defaultLineHeight, lines])

  const handleReadMore = () => {
    setExpanded(true)
  }

  const debouncedCheckOverflow = useDebounceCallback(checkOverflow, 150, {
    leading: false,
    trailing: true,
  })

  useEventListener('resize', debouncedCheckOverflow)

  useEffect(() => {
    checkOverflow()
  }, [content, lines, defaultLineHeight, checkOverflow])

  return (
    <div ref={containerRef}>
      <p
        ref={visibleRef}
        className={cn(
          'break-words whitespace-pre-wrap',
          !expanded && `line-clamp-${lines}`,
          className,
        )}
      >
        {content}
      </p>

      <p
        ref={measureRef}
        aria-hidden="false"
        className={cn(
          'pointer-events-none invisible absolute top-0 left-0 break-words whitespace-pre-wrap',
          className,
        )}
      >
        {content}
      </p>

      {isOverflow && !expanded && (
        <button
          type="button"
          onClick={handleReadMore}
          className={cn(
            'text-vive-primary-40 body1-omni-styles mt-2 hover:underline',
            readMoreButtonClassName,
          )}
        >
          {readMoreLabel}
        </button>
      )}
    </div>
  )
}
